/* ==========================================================================
   SERPROMAQ - SERVICIO DE GESTIÓN DE CATÁLOGO & FIREBASE FIRESTORE
   ========================================================================== */

import { 
  db, 
  storage, 
  collection, 
  doc, 
  getDocs, 
  setDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  onSnapshot, 
  query, 
  orderBy, 
  serverTimestamp, 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from './firebase-config.js';
import { CATALOG_ITEMS as DEFAULT_CATALOG_ITEMS } from '../data/catalog-data.js';

const COLLECTION_NAME = 'catalog_products';
const STORAGE_KEY = 'serpromaq_catalog_items_cache';

// Cargar catálogo inicial desde LocalStorage o predeterminado solo la primera vez
function loadInitialCatalog() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved !== null) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (e) {}
  return [];
}

let cachedItems = loadInitialCatalog();
let isFirestoreConnected = false;
const activeSubscribers = new Set();

function saveLocalCache(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {}
}

function notifySubscribers() {
  activeSubscribers.forEach(callback => {
    try {
      callback(cachedItems, isFirestoreConnected);
    } catch (e) {
      console.warn('Error en callback de suscriptor:', e);
    }
  });
}

/**
 * Obtener todos los productos actuales (Caché local o Firestore)
 */
export function getLoadedCatalogItems() {
  return cachedItems;
}

/**
 * Suscribirse a cambios en tiempo real en Firestore
 * @param {Function} onUpdateCallback - Se ejecuta con la lista actualizada de productos
 */
export function subscribeToCatalog(onUpdateCallback) {
  activeSubscribers.add(onUpdateCallback);
  
  // Notificar de inmediato con los datos actuales
  onUpdateCallback(cachedItems, isFirestoreConnected);

  try {
    const productsRef = collection(db, COLLECTION_NAME);
    const q = query(productsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const liveItems = [];
      snapshot.forEach((docSnap) => {
        liveItems.push({
          id: docSnap.id,
          ...docSnap.data()
        });
      });
      cachedItems = liveItems;
      saveLocalCache(cachedItems);
      isFirestoreConnected = true;
      notifySubscribers();
    }, (error) => {
      console.warn('Firestore snapshot en modo local:', error);
    });

    return () => {
      activeSubscribers.delete(onUpdateCallback);
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  } catch (err) {
    console.warn('Error inicializando Firestore listener:', err);
    return () => activeSubscribers.delete(onUpdateCallback);
  }
}

/**
 * Crear o actualizar un producto (con guardado local inmediato + sincronización segura)
 */
export async function saveProduct(productData, isEditing = false) {
  const generatedId = productData.id || `prod_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

  const payload = {
    id: generatedId,
    title: (productData.title || 'Solución Industrial SERPROMAQ').trim(),
    category: productData.category || 'general',
    badgeText: productData.badgeText || 'EQUIPO SERPROMAQ',
    badgeClass: productData.badgeClass || 'badge-green',
    highlightTag: productData.highlightTag || 'Garantía SERPROMAQ',
    image: productData.image || '/images/electric-forklift.jpg',
    availability: productData.availability || 'Disponible en Stock',
    specs: productData.specs || {},
    features: productData.features || [],
    description: (productData.description || 'Solución y maquinaria industrial garantizada por SERPROMAQ S.A.S.').trim(),
    updatedAt: new Date().toISOString()
  };

  // 1. Guardar de inmediato en memoria y LocalStorage para evitar cualquier congelamiento
  if (isEditing && productData.id) {
    const idx = cachedItems.findIndex(i => i.id === productData.id);
    if (idx !== -1) {
      cachedItems[idx] = { ...cachedItems[idx], ...payload, id: productData.id };
    } else {
      cachedItems.unshift(payload);
    }
  } else {
    cachedItems.unshift(payload);
  }

  saveLocalCache(cachedItems);
  notifySubscribers();

  // 2. Sincronizar en Firestore en segundo plano con límite de tiempo de 3.5s
  try {
    const firestorePayload = {
      ...payload,
      updatedAt: serverTimestamp()
    };
    if (!isEditing) {
      firestorePayload.createdAt = serverTimestamp();
    }

    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Firestore write timeout')), 3500)
    );

    const writePromise = (async () => {
      const docRef = doc(db, COLLECTION_NAME, payload.id);
      await setDoc(docRef, firestorePayload, { merge: true });
      return { success: true, id: payload.id };
    })();

    await Promise.race([writePromise, timeoutPromise]);
  } catch (err) {
    console.warn('Firestore sync advertencia (guardado en caché local activo):', err);
  }

  return { success: true, id: payload.id };
}

/**
 * Eliminar un producto
 */
export async function deleteProduct(productId) {
  cachedItems = cachedItems.filter(item => item.id !== productId);
  saveLocalCache(cachedItems);
  notifySubscribers();

  try {
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Firestore delete timeout')), 3500)
    );
    const docRef = doc(db, COLLECTION_NAME, productId);
    await Promise.race([deleteDoc(docRef), timeoutPromise]);
  } catch (error) {
    console.warn('Firestore delete sync advertencia:', error);
  }

  return { success: true };
}

/**
 * Actualizar rápidamente el estado de disponibilidad (1-clic)
 */
export async function updateProductAvailability(productId, newAvailability) {
  const item = cachedItems.find(i => i.id === productId);
  if (item) {
    item.availability = newAvailability;
    item.updatedAt = new Date().toISOString();
    saveLocalCache(cachedItems);
    notifySubscribers();
  }

  try {
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Firestore update timeout')), 3500)
    );
    const docRef = doc(db, COLLECTION_NAME, productId);
    await Promise.race([
      updateDoc(docRef, {
        availability: newAvailability,
        updatedAt: serverTimestamp()
      }),
      timeoutPromise
    ]);
  } catch (error) {
    console.warn('Firestore availability sync advertencia:', error);
  }

  return { success: true };
}

/**
 * Comprime una imagen en el cliente a máximo 1000px y formato optimizado
 */
function compressImageClient(file, maxWidth = 1000, quality = 0.82) {
  return new Promise((resolve) => {
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', quality));
          } catch (err) {
            resolve(e.target.result);
          }
        };
        img.onerror = () => resolve(e.target.result);
        img.src = e.target.result;
      };
      reader.onerror = () => resolve('/images/electric-forklift.jpg');
      reader.readAsDataURL(file);
    } catch (e) {
      resolve('/images/electric-forklift.jpg');
    }
  });
}

/**
 * Subir imagen de producto (con compresión rápida + timeout seguro de 3.5s)
 */
export async function uploadProductImage(file) {
  if (!file) return '/images/electric-forklift.jpg';

  // 1. Comprimir primero de forma ultrarrápida
  const optimizedBase64 = await compressImageClient(file);

  // 2. Intentar subir a Firebase Storage con límite de 3.5s
  try {
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Storage upload timeout')), 3500)
    );

    const uploadPromise = (async () => {
      const fileExt = file.name ? file.name.split('.').pop() : 'jpg';
      const fileName = `products/${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      return await getDownloadURL(snapshot.ref);
    })();

    const downloadURL = await Promise.race([uploadPromise, timeoutPromise]);
    return downloadURL;
  } catch (error) {
    console.warn('Storage no disponible o pausado. Usando imagen optimizada:', error);
    return optimizedBase64;
  }
}

/**
 * Sembrar/Cargar el catálogo inicial por defecto en Firestore con 1 clic
 */
export async function seedInitialCatalog() {
  try {
    const productsRef = collection(db, COLLECTION_NAME);
    let count = 0;

    for (const item of DEFAULT_CATALOG_ITEMS) {
      const { id, ...itemData } = item;
      const customDocRef = doc(productsRef, id);
      await setDoc(customDocRef, {
        ...itemData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      count++;
    }

    return { success: true, count };
  } catch (error) {
    console.error('Error sembrando catálogo inicial:', error);
    throw error;
  }
}
